// ONE white panel whose blocks are collapsible sections — the record History
// tab's look (src/history/RecordHistoryTab.tsx, BP-04), owned here so a tab
// that stacks several blocks supplies content only.
//
// Aaron 2026-10-08, on mustang workorders/337 › Aircraft (three white cards,
// teal headings outside them): "this should be 1 white area with collapsible
// sections the same as history etc".
//
// - CollapsibleSectionsPanel is the tab's one panel: the History panel
//   (HistoryGridBox — white, 16px radius, #ececec border, 15/50/50/50), whose
//   `.history-grid-box` rules in src/utils/global.css compact the accordions
//   (flush summary, black keyline under an open header, 8px top margin, flush
//   details). A block inside it never draws a card of its own.
// - CollapsibleSection is one block: History's day accordion (no gutters, no
//   elevation, no :before rule, divider underneath, #fafbfc header with the
//   expand chevron, title 600/14, an optional grey count after it).
//
// `body` names what the section holds, and sets the inset that content was
// laid out against when it had a card of its own:
//   "rows" — a flush list (History's days): no inset.
//   "form" — a general-tab form grid (Grid spacing={6} with `.section-padding`
//            items, BP-01): .mui-form's 50px top, which that grid's negative
//            top margin is measured against.
//   "grid" — an AeronetDataGridPremium: DetailPremiumBox's 30px top, which its
//            toolbar spacing is measured against.
// "form" and "grid" also leave 24px under the content before the divider.
//
// CollapsibleSectionsLead is an optional first block with no header (a record's
// identifying fields), laid out like a section's content.
//
// Sections start open; a section's content stays mounted while it is closed
// (MUI's Accordion default), so collapsing never drops unsaved input.
//
// Drift, reported not fixed: #fafbfc, #7d8285 and #dadada have no token in
// src/design/aeronet-tokens.css; they are byte-identical to what History and
// .mui-form already render.
import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { HistoryGridBox } from "./DatagridProContnet";

export type CollapsibleSectionBody = "rows" | "form" | "grid";

const BODY_INSET: Record<CollapsibleSectionBody, { pt: string; pb: string } | null> = {
  rows: null,
  form: { pt: "50px", pb: "24px" },
  grid: { pt: "30px", pb: "24px" },
};

/** .mui-form's outlined-field border (src/utils/global.css), for the forms the panel now holds. */
const fieldOutlineSx = {
  "& .MuiOutlinedInput-root:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline":
    { borderColor: "#dadada !important" },
};

/** Where the panel sits, which sets its gutters:
 *  "tab"   — a record tab: the History panel itself (HistoryGridBox, 15/50/50/50);
 *  "sheet" — inside a slide-in (the roster cell sheets, Pilot Roster mockup
 *            A812): the same white panel, border and accordion rules, with
 *            compact 4/16/16/16 gutters, since the sheet already frames it. */
export type CollapsibleSectionsInset = "tab" | "sheet";

/** The tab's one white panel; its CollapsibleSections stack inside it. */
export const CollapsibleSectionsPanel: React.FC<
  React.PropsWithChildren<{ testId?: string; inset?: CollapsibleSectionsInset }>
> = ({ testId, inset = "tab", children }) => (
  <Box data-testid={testId} data-collapsible-panel="true" data-inset={inset} sx={fieldOutlineSx}>
    {inset === "sheet" ? (
      // The History panel's class, so .history-grid-box's accordion rules apply.
      <Box
        className="history-grid-box"
        sx={{
          width: "100%",
          backgroundColor: "white",
          padding: "4px 16px 16px 16px",
          borderRadius: "16px",
          border: "1px solid #ececec",
        }}
      >
        {children}
      </Box>
    ) : (
      <HistoryGridBox>{children}</HistoryGridBox>
    )}
  </Box>
);

export interface CollapsibleSectionProps {
  /** The header text. */
  title: React.ReactNode;
  /** Grey text after the title, e.g. "(3 entries)". */
  count?: React.ReactNode;
  /** What the section holds — sets the content inset (see the file header). */
  body: CollapsibleSectionBody;
  /** Open on first render. Default true. */
  defaultExpanded?: boolean;
  /** Controlled open state, for a form that opens a section itself (Send for signature opens Approvals
   *  when the signed copies are chosen). Give onExpandedChange with it. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** On the section. */
  testId?: string;
  /** On the title. */
  titleTestId?: string;
}

/** A block at the top of the panel, above its sections, with no header and no collapse: a
 *  record's identifying fields (the Log Book entry's Owner / Operator / Form block, Shelley's
 *  layout, 2026-10-18). Same insets and divider as a CollapsibleSection's content. */
export const CollapsibleSectionsLead: React.FC<
  React.PropsWithChildren<{ body: CollapsibleSectionBody; testId?: string }>
> = ({ body, testId, children }) => {
  const inset = BODY_INSET[body];
  return (
    <Box data-testid={testId} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
      {inset ? <Box sx={inset}>{children}</Box> : children}
    </Box>
  );
};

/** One collapsible block of a CollapsibleSectionsPanel. */
export const CollapsibleSection: React.FC<React.PropsWithChildren<CollapsibleSectionProps>> = ({
  title,
  count,
  body,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  testId,
  titleTestId,
  children,
}) => {
  const inset = BODY_INSET[body];
  const controlled = expanded === undefined ? {} : { expanded, onChange: (_: unknown, open: boolean) => onExpandedChange?.(open) };
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      {...controlled}
      disableGutters
      elevation={0}
      data-testid={testId}
      sx={{
        "&:before": { display: "none" },
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#fafbfc" }}>
        <Typography data-testid={titleTestId} sx={{ fontWeight: 600, fontSize: 14 }}>
          {title}
        </Typography>
        {count != null && <Typography sx={{ ml: 1.5, fontSize: 13, color: "#7d8285" }}>{count}</Typography>}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>{inset ? <Box sx={inset}>{children}</Box> : children}</AccordionDetails>
    </Accordion>
  );
};
